import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchSessionPath, fetchSessionMeasurements, fetchSessionSims, type SessionMeasurementRow, type SessionPath, type SessionSimBreakdown } from '../api/sessions';
import SessionSimBreakdownPanel from '../components/SessionSimBreakdown';
import { downloadBlob, toCsv, toKml } from '../lib/exportSession';
import SessionMap, { type TripCoord } from '../components/SessionMap';
import SessionTimelineChart from '../components/SessionTimelineChart';
import MapLegend from '../components/MapLegend';

type Speed = 1 | 4 | 16 | 64;

export default function SessionDetailPage() {
  const { id } = useParams();
  const sessionId = id ?? '';
  const [path, setPath] = useState<SessionPath | null>(null);
  const [rows, setRows] = useState<SessionMeasurementRow[]>([]);
  const [err, setErr]   = useState<string | null>(null);
  const [cursorFraction, setCursorFraction] = useState(0); // 0..1
  const [sims, setSims] = useState<SessionSimBreakdown[]>([]);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(16);

  useEffect(() => {
    Promise.all([
      fetchSessionPath(sessionId).catch(() => null),
      fetchSessionMeasurements(sessionId).catch(() => []),
      // Absent on single-SIM drives and on sessions predating per-SIM storage,
      // so a failure here must not blank the whole page.
      fetchSessionSims(sessionId).catch(() => []),
    ]).then(([p, m, s]) => { setPath(p); setRows(m); setSims(s); })
      .catch((e) => setErr(String(e)));
  }, [sessionId]);

  const rawCoords = path?.features?.[0]?.geometry?.coordinates as unknown;
  const pathCoords: [number, number][] = Array.isArray(rawCoords)
    ? (Array.isArray(rawCoords[0])
        ? (rawCoords as [number, number][])
        : (rawCoords.length === 2 && typeof rawCoords[0] === 'number'
            ? [rawCoords as unknown as [number, number]]
            : []))
    : [];

  const heatmapPoints = useMemo(() => rows
    .filter((r) => r.lat != null && r.lon != null && r.rsrp != null)
    .map((r) => ({ lat: r.lat as number, lon: r.lon as number, value: r.rsrp as number })),
    [rows]);

  // Build time-stamped trip from rows: exclude measureNow POSTs (downloadMbps/mos set)
  // because their timestamp doesn't match their geo capture moment → stale, creates jumps.
  // Then sort, dedupe, drop teleports.
  const tripCoords: TripCoord[] = useMemo(() => {
    const v = rows
      .filter((r) => r.lat != null && r.lon != null && r.ts)
      .filter((r) => r.downloadMbps == null && r.uploadMbps == null && r.mos == null && r.latencyMs == null)
      .map((r) => ({ lon: r.lon as number, lat: r.lat as number, t: new Date(r.ts as string).getTime(), rsrp: r.rsrp }))
      .sort((a, b) => a.t - b.t);
    const out: TripCoord[] = [];
    const haversine = (a: TripCoord, b: TripCoord) => {
      const R = 6371000, toRad = (d: number) => d * Math.PI / 180;
      const dLat = toRad(b.lat - a.lat), dLon = toRad(b.lon - a.lon);
      const h = Math.sin(dLat/2)**2 + Math.sin(dLon/2)**2 * Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat));
      return 2 * R * Math.asin(Math.sqrt(h));
    };
    for (const p of v) {
      const last = out[out.length - 1];
      if (last && last.lon === p.lon && last.lat === p.lat) continue;
      if (last) {
        const dist = haversine(last, p);
        const dt = (p.t - last.t) / 1000;
        if (dt > 0 && dist / dt > 60) continue; // > 216 km/h = teleport
      }
      out.push(p);
    }
    return out;
  }, [rows]);

  const totalMs = useMemo(() => {
    if (tripCoords.length < 2) return 0;
    return tripCoords[tripCoords.length - 1].t - tripCoords[0].t;
  }, [tripCoords]);

  // RAF auto-advance when playing.
  const rafRef = useRef<number | null>(null);
  const lastFrameRef = useRef<number>(0);
  useEffect(() => {
    if (!playing || totalMs === 0) return;
    lastFrameRef.current = performance.now();
    const tick = (now: number) => {
      const dtReal = now - lastFrameRef.current; lastFrameRef.current = now;
      const dtSim = dtReal * speed;
      setCursorFraction((prev) => {
        const next = prev + dtSim / totalMs;
        if (next >= 1) { setPlaying(false); return 1; }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); };
  }, [playing, speed, totalMs]);

  if (err) return <div className="p-6 text-rose-400">Error: {err}</div>;
  if (!path) return <div className="p-6 text-slate-400">Loading session…</div>;

  const downloadCsv = () => downloadBlob(`session-${sessionId}.csv`, toCsv(rows), 'text/csv');
  const downloadKml = () => downloadBlob(`session-${sessionId}.kml`, toKml(rows, sessionId), 'application/vnd.google-earth.kml+xml');

  // For timeline chart cursor — derive idx from cursorFraction across rows-with-ts.
  const tsRows = rows.filter((r) => r.ts);
  const cursorIdx = Math.min(
    Math.max(0, Math.floor(cursorFraction * Math.max(1, tsRows.length - 1))),
    Math.max(0, tsRows.length - 1),
  );

  return (
    <div className="flex flex-col" style={{ position: 'absolute', inset: 0 }}>
      <div className="border-b border-slate-800 px-4 flex items-center gap-3 text-sm shrink-0" style={{ height: 48 }}>
        <Link to="/sessions" className="text-slate-400 hover:text-slate-200">‹ Sessions</Link>
        <span className="text-slate-200 font-mono text-xs">{sessionId.slice(0, 18)}…</span>
        <span className="text-slate-500 text-xs">{path.summary.startedAt ? new Date(path.summary.startedAt).toLocaleString() : ''}</span>
        <span className="text-slate-500 text-xs">{path.summary.pointCount} pts · {(path.summary.distanceM/1000).toFixed(2)} km</span>
        <div className="flex-1" />
        <button onClick={downloadCsv} className="bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold px-3 py-1.5 rounded-md">Download CSV</button>
        <button onClick={downloadKml} className="bg-blue-700 hover:bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-md">Download KML</button>
      </div>
      <div style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
        <SessionMap
          pathCoords={pathCoords}
          heatmapPoints={heatmapPoints}
          tripCoords={tripCoords}
          cursorFraction={cursorFraction}
        />
        <MapLegend metric="rsrp" cellCount={heatmapPoints.length} />
      </div>
      <SessionSimBreakdownPanel sims={sims} />
      <div className="border-t border-slate-800 bg-slate-950 p-3 flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => { if (cursorFraction >= 1) setCursorFraction(0); setPlaying(!playing); }}
            className="bg-blue-600 hover:bg-blue-500 text-white w-9 h-9 rounded-full flex items-center justify-center"
          >
            {playing ? '❚❚' : '▶'}
          </button>
          <input
            type="range"
            min={0}
            max={1000}
            value={Math.round(cursorFraction * 1000)}
            onChange={(e) => { setPlaying(false); setCursorFraction(Number(e.target.value) / 1000); }}
            className="flex-1"
          />
          <div className="flex gap-1">
            {([1, 4, 16, 64] as Speed[]).map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`text-xs font-bold px-2 py-1 rounded-md border ${speed === s ? 'bg-blue-900 border-blue-500 text-white' : 'bg-transparent border-slate-700 text-slate-400'}`}
              >{s}×</button>
            ))}
          </div>
        </div>
        <SessionTimelineChart
          rows={rows}
          cursorIdx={cursorIdx}
          onCursorChange={(i) => {
            setPlaying(false);
            const frac = tsRows.length <= 1 ? 0 : Math.min(1, Math.max(0, i / (tsRows.length - 1)));
            setCursorFraction(frac);
          }}
        />
      </div>
    </div>
  );
}
